import React, {useEffect} from 'react'
import { useTranslation } from 'react-i18next'
import {Badge, Form} from 'antd'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {useScreenType} from '@/hooks'
import {ROUTES} from '@/router/routes'
import {getRouterStore} from '@/router/utils'
import {PASSWORD_PATTERN} from '@/constants/patterns'
import {withTitle} from '@/router/utils'
import {ICreatePasswordStore} from './ICreatePasswordStore'
import {Button, Card, InputPassword, Popover} from '@/components'
import {RegistrationSuccessfulModal} from '../'
import {
    CONFIRM_NEW_PASSWORD_INPUT,
    SET_NEW_PASSWORD_BUTTON,
    SET_NEW_PASSWORD_INPUT
} from '@/constants/playwright-ids'
import BalloonsIcon from '~/assets/img/sign-up/balloons.svg'

import styles from './styles.scss'

export const CreatePasswordStoreSymbol = Symbol('CreatePasswordStore')

const CreatePassword = observer(() => {
    const {t} = useTranslation()
    const [form] = Form.useForm()
    const store = useInjection<ICreatePasswordStore>(CreatePasswordStoreSymbol)
    const {
        isLoading,
        email,
        refCode,
        verificationId,
        isRegistrationSuccessfulModalOpen,
        createPassword,
        goToLoginPage,
        checkSignupStepValidity
    } = store
    const {isMobile} = useScreenType()

    useEffect(() => {
        checkSignupStepValidity()
    }, [])

    const onFinish = () => {
        createPassword()
    }

    const formId = 'create-password'

    const popoverContent = () => {
        return (
            <div>
                {Object.entries(t('CreatePassword.codeRestrictions', { returnObjects: true })).map(([key, value]) => (
                    <div key={key}>
                        {store.restrictionErrors[key] ? (
                            <Badge status={'error'} />
                        ) : (
                            <Badge status={'success'} />
                        )}{' '}
                        {value}
                    </div>
                ))}
            </div>
        )
    }

    return (
        <>
            <div className={styles.setPasswordModalContainer}>
                <Card title={t('CreatePassword.title')}>
                    <Form
                        form={form}
                        id={formId}
                        layout={'vertical'}
                        requiredMark={false}
                        onFinish={onFinish}
                    >
                        <Popover
                            content={popoverContent()}
                            title={t('CreatePassword.codeMustContain')}
                            placement={isMobile ? 'bottom' : 'right'}
                        >
                            <Form.Item
                                label={t('CreatePassword.newPassword')}
                                name={'password'}
                                rules={[
                                    {
                                        required: true,
                                        message: t('CreatePassword.errors.newPasswordRequired')
                                    },
                                    {
                                        pattern: PASSWORD_PATTERN,
                                        message: t('CreatePassword.errors.newPasswordInvalid')
                                    }
                                ]}
                            >
                                <InputPassword
                                    onChange={(e) => store.setPassword(e.target.value)}
                                    placeholder={t('CreatePassword.newPassword')}
                                    data-test-id={SET_NEW_PASSWORD_INPUT}
                                />
                            </Form.Item>
                        </Popover>
                        <Form.Item
                            label={t('CreatePassword.confirmPassword')}
                            name={'confirm'}
                            rules={[
                                {
                                    required: true,
                                    message: t('CreatePassword.errors.confirmPasswordRequired')
                                },
                                {
                                    validator: (field, value) => {
                                        if (value === store.password) {
                                            return Promise.resolve()
                                        }

                                        return Promise.reject(
                                            new Error(t('CreatePassword.errors.confirmPasswordInvalid'))
                                        )
                                    }
                                }
                            ]}
                        >
                            <InputPassword
                                placeholder={t('CreatePassword.confirmPassword')}
                                data-test-id={CONFIRM_NEW_PASSWORD_INPUT}
                            />
                        </Form.Item>
                        <Form.Item key={'submit'}>
                            <Button
                                form={formId}
                                type={'primary'}
                                htmlType={'submit'}
                                className={styles.submit}
                                loading={isLoading}
                                data-test-id={SET_NEW_PASSWORD_BUTTON}
                            >
                                {t('CreatePassword.submit')}
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>
            </div>

            <RegistrationSuccessfulModal
                open={isRegistrationSuccessfulModalOpen}
                onOk={goToLoginPage}
                onCancel={goToLoginPage}
            />
            <img src={BalloonsIcon} style={{display: 'none'}} />
        </>
    )
})

export default withTitle(CreatePassword)
